import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigurationStep } from '~/app/compositions/sidebar/configuration-step/configuration-step';
import { OptionsHeader } from '~/app/compositions/sidebar/options-header';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { useMoreInformationDialog } from '~/app/pages/configurator/components/more-information-dialog';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useDebugMode } from '~/app/utils/debug-mode';
import { ExtendedSelectionGroupWrapper } from '~/components/selection-card-new/extended-selection-group';
import { hasTeaserText } from '~/components/selection-card-new/selection-card';
import { useActiveCard } from '~/components/slider-panel';
import { ConfiguredCarCatalogImage, ConfiguredCarPackageFragment } from '~/graphql/__generated__/graphql';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useImages } from '~/graphql/hooks/use-images';
import { useSteps } from '~/graphql/hooks/use-steps';
import { CatalogItem } from '~/graphql/mapper';
import { FeatureCategory, FeatureOrPackage, SelectableOption, SelectionCardItem } from '~/graphql/types';
import { TECHNOLOGY_PACKAGES } from '~/utils/family-constants';

export function Packages() {
	const { i18n } = useI18n();
	const { packages: allPackages } = useCatalog();
	const { config } = useMarketConfig();
	const { feature } = useVATConfig();
	const { isLoading, loadingFlags, isItemLoading, isItemChecked } = useConfigurator();
	const { openMoreInformationDialog } = useMoreInformationDialog();
	const { selectCard } = useActiveCard();
	const { getName } = useDebugMode();
	const [imageUrls, setImageUrls] = useState<(string[] | null | undefined)[]>([]);
	const [media, setMedia] = useState<Array<Array<ConfiguredCarCatalogImage | null | undefined>>>([]);
	const { mediaByProfileId } = useImages();

	const packages = useMemo(
		() =>
			allPackages?.filter(
				(item) => (item.__item as ConfiguredCarPackageFragment).family.id !== TECHNOLOGY_PACKAGES,
			),
		[allPackages],
	);
	const { step, total } = useSteps('packages');

	const trackSelectionClickEvent = useCallback(
		(
			eventName: string,
			label: string,
			item: FeatureOrPackage,
			elementName: string,
			value: string,
			selected?: boolean,
			eventAction = EventAction.InternalLink,
		) => {
			appTrackingService.trackSelectionClickEvent({
				eventAction,
				eventName,
				label,
				elementName,
				value,
				category: 'packages',
				elementCurrentStep: step,
				elementTotalSteps: total,
				selected,
				item,
			});
		},
		[step, total],
	);

	const items = useMemo(() => {
		if (!packages) return [];

		return packages.map((packageItem, index): SelectionCardItem => {
			return {
				name: getName(packageItem) ?? packageItem.name,
				description: hasTeaserText(packageItem.__item.texts?.teaserText)
					? packageItem.__item.texts?.teaserText
					: null,
				key: packageItem.pr7,
				loading: isItemLoading(packageItem),
				selected: isItemChecked(packageItem),
				readonly: loadingFlags.checkingConflicts || !packageItem.status.available,
				onSelect: () =>
					selectCard(index, packageItem as CatalogItem, () =>
						trackSelectionClickEvent(
							'click to change configuration',
							packageItem.name || '',
							packageItem.__item as FeatureOrPackage,
							'card',
							'selection feature app: packages: all',
							isItemChecked(packageItem),
						),
					),
				standard: packageItem.status.standard,
				galleryImageUrls: packageItem?.galleryImages || [],
				galleryMedia: packageItem?.galleryMedia || [],
				price:
					config.showPrices || !config.hideMonthlyRates
						? {
								totalPrice: config.showPrices ? packageItem.totalPrice : undefined,
								feature,
							}
						: undefined,
				textLink: {
					label: i18n('ngc.actions.moreInfo'),
					onTextLinkClick: () =>
						openMoreInformationDialog(
							packageItem as SelectableOption<FeatureCategory>,
							packageItem.__item,
							() =>
								trackSelectionClickEvent(
									'click to change configuration',
									packageItem.name || '',
									packageItem.__item,
									'button',
									'details layer',
									isItemChecked(packageItem),
								),
							step,
							total,
							'packages',
						),
				},
				onDescriptionToggle: (showDescription: boolean) =>
					trackSelectionClickEvent(
						`click to ${showDescription ? 'close' : 'open'} details accordion`,
						showDescription ? i18n('ngc.actions.hideDetails') : i18n('ngc.actions.showDetails'),
						packageItem.__item,
						'text link',
						'details accordion',
						undefined,
						EventAction.Content,
					),
				disclaimerContent: packageItem.__item.texts?.disclaimerText || undefined,
				disclaimerHeadline: packageItem.__item.texts?.disclaimerHeadline || undefined,
				disclaimerStartDate: packageItem.__item.texts?.disclaimerStartDate || undefined,
				disclaimerEndDate: packageItem.__item.texts?.disclaimerEndDate || undefined,
			};
		});
	}, [
		packages,
		getName,
		isItemLoading,
		isItemChecked,
		loadingFlags.checkingConflicts,
		config.showPrices,
		config.hideMonthlyRates,
		feature,
		i18n,
		selectCard,
		trackSelectionClickEvent,
		openMoreInformationDialog,
		step,
		total,
	]);

	useEffect(() => {
		const selectedItems = packages?.filter((a) => a.status.selected);
		const images = selectedItems?.map((a) => a.galleryImages) as string[][];
		const newImages = images?.filter((a) => {
			return !imageUrls.find((b) => {
				return JSON.stringify(b) === JSON.stringify(a);
			});
		});

		const buffer = imageUrls.filter((a) => images.find((b) => JSON.stringify(b) === JSON.stringify(a)));
		if (newImages) {
			setImageUrls([...newImages, ...buffer]);
		}

		const structuredMedia: Array<Array<ConfiguredCarCatalogImage | null | undefined>> = [];

		selectedItems?.forEach((itm) => {
			if (Array.isArray(itm.galleryMedia)) {
				structuredMedia.push(itm.galleryMedia);
			} else {
				structuredMedia.push([itm.galleryMedia]);
			}
		});

		setMedia(structuredMedia);
	}, [JSON.stringify(imageUrls), JSON.stringify(packages)]);

	const displayMedia = useMemo(() => {
		const defaultMedia = [
			mediaByProfileId[ImageViewsEnum.EXTERIOR_FRONT_3_4],
			mediaByProfileId[ImageViewsEnum.INTERIOR_SEATS_SEATS],
		];
		return media && media.length > 0 ? [...media, defaultMedia] : [defaultMedia];
	}, [JSON.stringify(media), JSON.stringify(mediaByProfileId)]);

	const impressionTrackingRef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading && total) {
				appTrackingService.selectionStepImpression({
					node,
					value: 'packages',
					elementCurrentStep: step,
					elementTotalSteps: total,
				});
			}
		},
		[isLoading],
	);

	const categoryDisclaimer = config.categoryDisclaimers?.find((d) => d.step === 'packages')?.disclaimer;

	return (
		<ConfigurationStep
			ref={impressionTrackingRef}
			hide={items.length === 0}
			id={'packages'}
			media={displayMedia}
			headline={<OptionsHeader id={'packages'} title={i18n('ngc.equipment.package')} step={step} total={total} />}
			disclaimer={categoryDisclaimer}>
			<ExtendedSelectionGroupWrapper type="multi-select" items={items} />
		</ConfigurationStep>
	);
}
